import { useEffect, useState } from 'react';
import { WEIGHT_UNIT } from '../../../../../../types/enum';
import { convertWeight } from '../../../../../../lib/utils';
import ReactPlayer from 'react-player';
import BottomDialog from '../../../../../../components/bottomDialog';
import CompleteStamp from '../../complete';
import request from '../../../../../../lib/request';
import usePreference from '../../../../../../hook/usePreference';

const CycleRoutine = ({
  routine,
  list,
  readyCount,
  setReadyCount,
  preference,
  exerciseCount,
}) => {
  return (
    <>
      <h2>
        <span className='font-bold'>{routine?.title}</span>{' '}
        <span className='opacity-40 text-xs'>({exerciseCount} exercises)</span>
      </h2>

      <div className='max-h-96 overflow-y-scroll pb-2 my-2 rounded-xl space-y-2'>
        {list.map((item, i) => (
          <div
            key={i}
            className='flex items-center gap-2'
          >
            <div className='relative w-20 h-24 overflow-hidden flex justify-center items-center rounded-xl'>
              <ReactPlayer
                url={item.exerciseDetail.exercise.vodUrl}
                width='100%'
                muted={true}
                loop={true}
                playsinline={true}
                controls={true}
                config={{
                  attributes: {
                    controlsList: 'nodownload',
                  },
                }}
                onProgress={(params) => {
                  if (
                    params.played === 0 &&
                    params.loaded === 1 &&
                    exerciseCount !== readyCount
                  ) {
                    setReadyCount((prev) => prev + 1);
                  }
                }}
                style={{
                  opacity: exerciseCount === readyCount ? 1 : 0.4,
                }}
              />
            </div>
            <div>
              <div className='uppercase'>
                {item.exerciseDetail.exercise.name}
              </div>
              <div className='opacity-40'>
                {item.exerciseDetail.mode === 'REPS' && 'x'}{' '}
                {item.exerciseDetail.mode === 'TIME'
                  ? `${Math.floor(item.modeValue / 60)}:${item.modeValue % 60}`
                  : item.modeValue}
              </div>
              <div>
                {item.exerciseDetail.dropset && (
                  <span className='text-[10px] bg-black bg-opacity-60 text-white rounded-xl px-2 italic mr-[2px] py-[2px]'>
                    Dropset
                  </span>
                )}
                {item.exerciseDetail.maxout && (
                  <span className='text-[10px] bg-black bg-opacity-60 text-white rounded-xl px-2 italic mr-[2px] py-[2px]'>
                    Max-out
                  </span>
                )}
                {item.weight ? (
                  <span className='text-[10px] bg-black bg-opacity-60 text-white rounded-xl px-2 italic py-[2px]'>
                    {preference.weightUnit === WEIGHT_UNIT.KG
                      ? `${item.weight} kg`
                      : `${convertWeight(
                          item.weight,
                          WEIGHT_UNIT.KG,
                          WEIGHT_UNIT.LB
                        )} lb`}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const SetRoutine = ({
  routine,
  list,
  readyCount,
  setReadyCount,
  preference,
  exerciseCount,
}) => {
  return (
    <>
      <h2>
        <span className='font-bold'>{routine?.title}</span>{' '}
        <span className='opacity-40 text-xs'>({exerciseCount} exercises)</span>
      </h2>
      <div className='max-h-96 overflow-y-scroll pb-2 my-2 rounded-xl'>
        {list.map((item, i) => (
          <div key={i}>
            <div className='font-semibold mt-4 mb-2'>
              Exercise {i + 1}{' '}
              <span className='opacity-40 text-xs'>x {item.sets} sets</span>
            </div>
            <div className='space-y-2'>
              {item.exercises.map((exe, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2`}
                >
                  <div className='relative w-20 h-24 overflow-hidden flex justify-center items-center rounded-xl'>
                    <ReactPlayer
                      url={exe.exerciseDetail.exercise.vodUrl}
                      width='100%'
                      muted={true}
                      loop={true}
                      playsinline={true}
                      controls={true}
                      config={{
                        attributes: {
                          controlsList: 'nodownload',
                        },
                      }}
                      onProgress={(params) => {
                        if (
                          params.played === 0 &&
                          params.loaded === 1 &&
                          exerciseCount !== readyCount
                        ) {
                          setReadyCount((prev) => prev + 1);
                        }
                      }}
                      style={{
                        opacity: exerciseCount === readyCount ? 1 : 0.4,
                      }}
                    />
                  </div>
                  <div>
                    <div className='uppercase'>
                      {exe.exerciseDetail.exercise.name}
                    </div>
                    <div className='opacity-40'>
                      {exe.exerciseDetail.mode === 'REPS' && 'x'}{' '}
                      {exe.exerciseDetail.mode === 'TIME'
                        ? `${Math.floor(exe.modeValue / 60)}:${
                            exe.modeValue % 60
                          }`
                        : exe.modeValue}
                    </div>
                    <div>
                      {exe.exerciseDetail.dropset && (
                        <span className='text-[10px] bg-black bg-opacity-60 text-white rounded-xl px-2 italic mr-[2px] py-[2px]'>
                          Dropset
                        </span>
                      )}
                      {exe.exerciseDetail.maxout && (
                        <span className='text-[10px] bg-black bg-opacity-60 text-white rounded-xl px-2 italic mr-[2px] py-[2px]'>
                          Max-out
                        </span>
                      )}
                      {exe.weight ? (
                        <span className='text-[10px] bg-black bg-opacity-60 text-white rounded-xl px-2 italic py-[2px]'>
                          {preference.weightUnit === WEIGHT_UNIT.KG
                            ? `${exe.weight} kg`
                            : `${convertWeight(
                                exe.weight,
                                WEIGHT_UNIT.KG,
                                WEIGHT_UNIT.LB
                              )} lb`}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const WorkoutBottomDialog = ({
  dialogRef,
  closeBottomDialog,
  startWorkout,
  isSelectedWorkoutDone,
  challenge,
  selectedWorkout,
  open,
  selectedDayNumber,
  selectedDay,
}) => {
  const { preference } = usePreference();

  const [readyCount, setReadyCount] = useState(0);
  const [routine, setRoutine] = useState(null);
  const [cycleGroups, setCycleGroups] = useState([]);
  const [nonCycleGroups, setNonCycleGroups] = useState([]);
  const [exerciseCount, setExerciseCount] = useState(0);

  useEffect(() => {
    if (open) {
      getDayRoutine();
      setReadyCount(0);
    } else {
      setReadyCount(0);
      setCycleGroups([]);
      setNonCycleGroups([]);
      setExerciseCount(0);
    }
  }, [open]);

  const getDayRoutine = async () => {
    try {
      const { result } = await request.get(
        `/challenge/${challenge.challengeId}/day/${selectedDayNumber}/routine${
          challenge.category === 'CUSTOM'
            ? `?category=CUSTOM&targetId=${challenge.id}`
            : ''
        }`
      );
      if (result && result.routine?.groups) {
        setRoutine(result.routine);

        if (result.routine.cycleRoutine) {
          const newGroups = result.routine.groups.reduce((acc, cur) => {
            return [...acc, ...cur.exerciseSet.sets];
          }, []);

          setCycleGroups(newGroups);
          setExerciseCount(newGroups.length);
        } else {
          const newGroups = [];
          result.routine.groups.reduce((acc, cur, i) => {
            if (Object.keys(acc).length === 0) {
              acc.exerciseIds = cur.exerciseSet.sets.map(
                (set) => set.exerciseDetail.exercise.id
              );
              acc.exercises = cur.exerciseSet.sets;
              acc.sets = 1;

              if (i === result.routine.groups.length - 1) {
                newGroups.push({ ...acc });
              }
              return acc;
            } else {
              if (
                acc.exerciseIds.length === cur.exerciseSet.sets.length &&
                acc.exerciseIds.every(
                  (id, j) =>
                    id === cur.exerciseSet.sets[j].exerciseDetail.exercise.id
                )
              ) {
                acc.sets++;
                if (i === result.routine.groups.length - 1) {
                  newGroups.push({ ...acc });
                }
                return acc;
              } else {
                newGroups.push({ ...acc });

                const newAcc = {
                  exerciseIds: cur.exerciseSet.sets.map(
                    (set) => set.exerciseDetail.exercise.id
                  ),
                  exercises: cur.exerciseSet.sets,
                  sets: 1,
                };
                if (i === result.routine.groups.length - 1) {
                  newGroups.push({ ...newAcc });
                }

                return newAcc;
              }
            }
          }, {});

          setNonCycleGroups(newGroups);
          setExerciseCount(
            newGroups.reduce((acc, cur) => acc + cur.exercises.length, 0)
          );
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleClose = () => {
    closeBottomDialog();
  };

  return (
    <BottomDialog
      dialogRef={dialogRef}
      closeDialog={handleClose}
    >
      {!isSelectedWorkoutDone && (
        <div>
          {routine?.cycleRoutine ? (
            <CycleRoutine
              routine={routine}
              list={cycleGroups}
              readyCount={readyCount}
              setReadyCount={setReadyCount}
              preference={preference}
              exerciseCount={exerciseCount}
            />
          ) : (
            <SetRoutine
              routine={routine}
              list={nonCycleGroups}
              readyCount={readyCount}
              setReadyCount={setReadyCount}
              preference={preference}
              exerciseCount={exerciseCount}
            />
          )}
        </div>
      )}
      <div className='flex flex-col items-center gap-2'>
        {isSelectedWorkoutDone ? (
          <CompleteStamp
            challengeTitle={challenge.title}
            workoutTitle={selectedWorkout?.title ?? ''}
            selectedDay={selectedDay}
          />
        ) : (
          <button
            className={`border rounded-xl w-full py-2 bg-green-500 text-green-600 bg-opacity-20 border-green-500 border-opacity-60`}
            onClick={() => {
              startWorkout();
            }}
          >
            Start Workout
          </button>
        )}
        <button
          className='border rounded-xl w-full py-2 mt-2'
          onClick={handleClose}
        >
          Cancel
        </button>
      </div>
    </BottomDialog>
  );
};

export default WorkoutBottomDialog;
